import { FC, useCallback, useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import styled from "styled-components";

const libraries: ("places" | "drawing" | "geometry" | "visualization")[] = ["places"];

interface AddressComponents {
    state?: string;
    city?: string;
    zipCode?: string;
}

interface GoogleMapPickerProps {
    isOpen: boolean;
    onClose: () => void;
    onLocationSelect: (address: string, lat: number, lng: number, googleMapUrl: string, addressComponents?: AddressComponents) => void;
    initialLocation?: { lat: number; lng: number };
    initialAddress?: string;
}

const MapModal = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  margin: auto;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 2rem;
  padding: 2rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: auto;
  outline: none;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 500px;
  margin-top: 1rem;
  border-radius: 1rem;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h2 {
    margin: 0;
    color: #808d9e;
    font-weight: 600;
  }
  
  button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    
    img {
      width: 24px;
      height: 24px;
    }
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
  
  input {
    width: 100%;
    height: 5.2rem;
    padding: 0 1rem;
    background-color: var(--light-gray);
    border-radius: 5px;
    outline: none;
    border: none;
    font-size: 1.2rem;
    
    &:focus {
      border: 2px solid var(--primary);
    }
  }
  
  /* Style Google Places Autocomplete suggestions */
  .pac-container {
    font-size: 1.2rem !important;
    border-radius: 5px !important;
    margin-top: 0.25rem !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    
    .pac-item {
      padding: 1rem 1.2rem !important;
      font-size: 1.2rem !important;
      line-height: 1.5 !important;
      cursor: pointer !important;
      
      .pac-item-query {
        font-size: 1.2rem !important;
        padding-bottom: 0.3rem !important;
      }
      
      &:hover {
        background-color: #f5f5f5 !important;
      }
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  
  &.primary {
    background-color: var(--primary);
    color: white;
  }
  
  &.secondary {
    background-color: #e6e6e6;
    color: #808d9e;
  }
`;

const GoogleMapPicker: FC<GoogleMapPickerProps> = ({
    isOpen,
    onClose,
    onLocationSelect,
    initialLocation,
    initialAddress,
}) => {
    const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
        initialLocation || null
    );
    const [address, setAddress] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const [addressComponents, setAddressComponents] = useState<AddressComponents>({});
    const searchInputRef = useRef<HTMLInputElement>(null);

    const extractAddressComponents = (result: google.maps.GeocoderResult): AddressComponents => {
        const components: AddressComponents = {};

        if (result.address_components) {
            result.address_components.forEach((component) => {
                const types = component.types;

                if (types.includes("administrative_area_level_1") && !components.state) {
                    components.state = component.long_name;
                }

                if (types.includes("locality") && !components.city) {
                    components.city = component.long_name;
                } else if (types.includes("administrative_area_level_2") && !components.city) {
                    components.city = component.long_name;
                }

                if (types.includes("postal_code") && !components.zipCode) {
                    components.zipCode = component.long_name;
                }
            });
        }

        return components;
    };

    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });

    useEffect(() => {
        if (isLoaded && isOpen && searchInputRef.current && !autocomplete && window.google) {
            const autocompleteInstance = new window.google.maps.places.Autocomplete(
                searchInputRef.current,
                {
                    types: ["establishment", "geocode"],
                    fields: ["geometry", "formatted_address", "name", "place_id", "address_components"],
                }
            );

            autocompleteInstance.addListener("place_changed", () => {
                const place = autocompleteInstance.getPlace();

                if (place.geometry && place.geometry.location) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const location = { lat, lng };

                    setSelectedLocation(location);
                    setAddress(place.formatted_address || "");

                    if (place.address_components) {
                        const components: AddressComponents = {};
                        place.address_components.forEach((component) => {
                            const types = component.types;

                            if (types.includes("administrative_area_level_1") && !components.state) {
                                components.state = component.long_name;
                            }

                            if (types.includes("locality") && !components.city) {
                                components.city = component.long_name;
                            } else if (types.includes("administrative_area_level_2") && !components.city) {
                                components.city = component.long_name;
                            }

                            if (types.includes("postal_code") && !components.zipCode) {
                                components.zipCode = component.long_name;
                            }
                        });
                        setAddressComponents(components);
                    }

                    if (map) {
                        map.panTo(location);
                        map.setZoom(15);
                    }
                }
            });

            setAutocomplete(autocompleteInstance);
        }

        return () => {
            if (!isOpen && autocomplete) {
                setAutocomplete(null);
            }
        };
    }, [isLoaded, isOpen, autocomplete, map]);

    useEffect(() => {
        if (isOpen && initialLocation) {
            setSelectedLocation(initialLocation);

            if (initialAddress) {
                setAddress(initialAddress);
                if (searchInputRef.current) {
                    searchInputRef.current.value = initialAddress;
                }
            }

            if (isLoaded && window.google) {
                setIsLoading(true);
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: initialLocation })
                    .then((response) => {
                        if (response.results && response.results.length > 0) {
                            const formattedAddress = response.results[0].formatted_address;
                            setAddress(formattedAddress);
                            if (searchInputRef.current) {
                                searchInputRef.current.value = formattedAddress;
                            }
                        } else {
                            const coordAddress = `${initialLocation.lat}, ${initialLocation.lng}`;
                            if (!initialAddress) {
                                setAddress(coordAddress);
                                if (searchInputRef.current) {
                                    searchInputRef.current.value = coordAddress;
                                }
                            }
                        }
                    })
                    .catch((error) => {
                        console.error("Error geocoding:", error);
                        const coordAddress = `${initialLocation.lat}, ${initialLocation.lng}`;
                        if (!initialAddress) {
                            setAddress(coordAddress);
                            if (searchInputRef.current) {
                                searchInputRef.current.value = coordAddress;
                            }
                        }
                    })
                    .finally(() => setIsLoading(false));
            } else {
                if (!initialAddress) {
                    const coordAddress = `${initialLocation.lat}, ${initialLocation.lng}`;
                    setAddress(coordAddress);
                    if (searchInputRef.current) {
                        searchInputRef.current.value = coordAddress;
                    }
                }
            }
        } else if (isOpen && !initialLocation) {
            setSelectedLocation(null);
            setAddress("");
            if (searchInputRef.current) {
                searchInputRef.current.value = "";
            }
        }
    }, [isOpen, initialLocation, initialAddress, isLoaded]);

    const mapContainerStyle = {
        width: "100%",
        height: "100%",
    };

    const defaultCenter = {
        lat: initialLocation?.lat || 40.7128,
        lng: initialLocation?.lng || -74.0060,
    };

    const handleMapClick = useCallback(
        async (e: google.maps.MapMouseEvent) => {
            if (e.latLng) {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                setSelectedLocation({ lat, lng });

                setIsLoading(true);
                try {
                    const geocoder = new window.google.maps.Geocoder();
                    const response = await geocoder.geocode({ location: { lat, lng } });

                    if (response.results && response.results.length > 0) {
                        const result = response.results[0];
                        setAddress(result.formatted_address);
                        const components = extractAddressComponents(result);
                        setAddressComponents(components);
                    } else {
                        setAddress(`${lat}, ${lng}`);
                        setAddressComponents({});
                    }
                } catch (error) {
                    console.error("Error geocoding:", error);
                    setAddress(`${lat}, ${lng}`);
                    setAddressComponents({});
                } finally {
                    setIsLoading(false);
                }
            }
        },
        []
    );

    const handleConfirm = () => {
        if (selectedLocation) {
            const googleMapUrl = `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`;
            onLocationSelect(
                address || `${selectedLocation.lat}, ${selectedLocation.lng}`,
                selectedLocation.lat,
                selectedLocation.lng,
                googleMapUrl,
                addressComponents
            );
            onClose();
        }
    };

    const handleClose = () => {
        setSelectedLocation(initialLocation || null);
        setAddress("");
        if (searchInputRef.current) {
            searchInputRef.current.value = "";
        }
        onClose();
    };

    return (
        <MapModal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Select Location on Map"
            ariaHideApp={false}
        >
            <Header>
                <h2>Select Store Location</h2>
                <button onClick={handleClose}>
                    <img src={close} alt="Close" />
                </button>
            </Header>

            {!GOOGLE_MAPS_API_KEY ? (
                <div style={{ padding: "2rem", textAlign: "center", color: "#808d9e" }}>
                    <p>Google Maps API key is not configured.</p>
                    <p style={{ fontSize: "0.9rem", marginTop: "0.5rem" }}>
                        Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.
                    </p>
                </div>
            ) : !isLoaded ? (
                <div>Loading map...</div>
            ) : (
                <>
                    <style>
                        {`
              .pac-container {
                font-size: 1.4rem !important;
                border-radius: 5px !important;
                margin-top: 0.25rem !important;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
                z-index: 2000 !important;
              }
              
              .pac-item {
                padding: 1.2rem 1.5rem !important;
                font-size: 1.4rem !important;
                line-height: 1.6 !important;
                cursor: pointer !important;
                border-top: 1px solid #e0e0e0 !important;
              }
              
              .pac-item:first-child {
                border-top: none !important;
              }
              
              .pac-item-query {
                font-size: 1.4rem !important;
                padding-bottom: 0.4rem !important;
                font-weight: 500 !important;
              }
              
              .pac-matched {
                font-size: 1.4rem !important;
                font-weight: 600 !important;
              }
              
              .pac-item-query .pac-matched {
                font-size: 1.4rem !important;
              }
              
              .pac-item * {
                font-size: 1.4rem !important;
              }
              
              .pac-item-query span {
                font-size: 1.4rem !important;
              }
              
              .pac-item:hover {
                background-color: #f5f5f5 !important;
              }
              
              .pac-icon {
                margin-right: 1rem !important;
                width: 20px !important;
                height: 20px !important;
              }
            `}
                    </style>
                    <SearchContainer>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search for a location..."
                            autoComplete="off"
                        />
                    </SearchContainer>

                    <MapContainer>
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={selectedLocation || initialLocation || defaultCenter}
                            zoom={selectedLocation || initialLocation ? 15 : 10}
                            onClick={handleMapClick}
                            onLoad={(map) => {
                                setMap(map);
                                if (initialLocation && !selectedLocation) {
                                    map.panTo(initialLocation);
                                    map.setZoom(15);
                                    setSelectedLocation(initialLocation);
                                }
                            }}
                            options={{
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: true,
                            }}
                        >
                            {(selectedLocation || initialLocation) && (
                                <Marker
                                    position={selectedLocation || initialLocation!}
                                    draggable={true}
                                    onDragEnd={(e) => {
                                        if (e.latLng) {
                                            const lat = e.latLng.lat();
                                            const lng = e.latLng.lng();
                                            setSelectedLocation({ lat, lng });

                                            setIsLoading(true);
                                            const geocoder = new window.google.maps.Geocoder();
                                            geocoder.geocode({ location: { lat, lng } })
                                                .then((response) => {
                                                    if (response.results && response.results.length > 0) {
                                                        const result = response.results[0];
                                                        setAddress(result.formatted_address);
                                                        const components = extractAddressComponents(result);
                                                        setAddressComponents(components);
                                                    } else {
                                                        setAddress(`${lat}, ${lng}`);
                                                        setAddressComponents({});
                                                    }
                                                })
                                                .catch((error) => {
                                                    console.error("Error geocoding:", error);
                                                    setAddress(`${lat}, ${lng}`);
                                                    setAddressComponents({});
                                                })
                                                .finally(() => setIsLoading(false));
                                        }
                                    }}
                                />
                            )}
                        </GoogleMap>
                    </MapContainer>

                    {isLoading && <div style={{ marginTop: "1rem" }}>Loading address...</div>}
                    {address && !isLoading && (
                        <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f5f5", borderRadius: "5px" }}>
                            <strong>Selected Address:</strong> {address}
                        </div>
                    )}

                    <ButtonContainer>
                        <Button className="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            className="primary"
                            onClick={handleConfirm}
                            disabled={!selectedLocation || isLoading}
                        >
                            Confirm Location
                        </Button>
                    </ButtonContainer>
                </>
            )}
        </MapModal>
    );
};

export default GoogleMapPicker;

